
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ProspectFormData } from './types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { CardContent } from '@/components/ui/card';

export interface ProspectFormFieldsProps {
  form: UseFormReturn<ProspectFormData, any, undefined>;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
}

const ProspectFormFields: React.FC<ProspectFormFieldsProps> = ({ 
  form, 
  onSubmit, 
  isSubmitting 
}) => {
  return (
    <form onSubmit={onSubmit}>
      <CardContent className="space-y-4">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input 
              id="firstName"
              {...form.register("firstName", { required: "First name is required" })} 
              placeholder="First name"
              className={form.formState.errors.firstName ? "border-red-500" : ""}
            />
            {form.formState.errors.firstName && <p className="text-red-500 text-sm">{form.formState.errors.firstName.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input 
              id="lastName"
              {...form.register("lastName", { required: "Last name is required" })} 
              placeholder="Last name"
              className={form.formState.errors.lastName ? "border-red-500" : ""}
            />
            {form.formState.errors.lastName && <p className="text-red-500 text-sm">{form.formState.errors.lastName.message}</p>}
          </div>
        </div>
        
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email"
              type="email"
              {...form.register("email", { 
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              })} 
              placeholder="Email address"
              className={form.formState.errors.email ? "border-red-500" : ""}
            />
            {form.formState.errors.email && <p className="text-red-500 text-sm">{form.formState.errors.email.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input 
              id="phone"
              {...form.register("phone")} 
              placeholder="Phone number"
              className={form.formState.errors.phone ? "border-red-500" : ""}
            />
            {form.formState.errors.phone && <p className="text-red-500 text-sm">{form.formState.errors.phone.message}</p>}
          </div>
        </div>
        
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input 
              id="company"
              {...form.register("company")} 
              placeholder="Company"
              className={form.formState.errors.company ? "border-red-500" : ""}
            />
            {form.formState.errors.company && <p className="text-red-500 text-sm">{form.formState.errors.company.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Select {...form.register("industry")}>
              <SelectTrigger>
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="technology">Technology</SelectItem>
                <SelectItem value="healthcare">Healthcare</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="education">Education</SelectItem>
                <SelectItem value="retail">Retail</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {form.formState.errors.industry && <p className="text-red-500 text-sm">{form.formState.errors.industry.message}</p>}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="stage">Stage</Label>
          <Select {...form.register("stage", { required: "Stage is required" })}>
            <SelectTrigger>
              <SelectValue placeholder="Select stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lead">Lead</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="proposal">Proposal</SelectItem>
              <SelectItem value="negotiation">Negotiation</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          {form.formState.errors.stage && <p className="text-red-500 text-sm">{form.formState.errors.stage.message}</p>}
        </div>
        
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="value">Potential Value ($)</Label>
            <Input 
              id="value"
              type="number"
              {...form.register("value", {
                valueAsNumber: true,
                min: { value: 0, message: "Value cannot be negative" }
              })} 
              placeholder="0"
              className={form.formState.errors.value ? "border-red-500" : ""}
            />
            {form.formState.errors.value && <p className="text-red-500 text-sm">{form.formState.errors.value.message}</p>}
          </div>
        </div>
      </CardContent>
    </form>
  );
};

export default ProspectFormFields;
